import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  teamMemberCreateSchema,
  type TeamMemberCreateInput,
  type TeamMember,
} from '@inafana/shared';
import { api, ApiError } from '@/lib/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';

const EMPTY: TeamMemberCreateInput = {
  name: '',
  role: '',
  bio: '',
  photoUrl: null,
  order: 0,
  isPublished: true,
};

export function TeamAdmin(): React.JSX.Element {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'team'],
    queryFn: () => api<{ members: TeamMember[] }>('/admin/team'),
  });

  const form = useForm<TeamMemberCreateInput>({
    resolver: zodResolver(teamMemberCreateSchema),
    defaultValues: EMPTY,
  });

  const onOpen = (m: TeamMember | null): void => {
    setEditing(m);
    form.reset(
      m
        ? {
            name: m.name,
            role: m.role,
            bio: m.bio,
            photoUrl: m.photoUrl,
            order: m.order,
            isPublished: m.isPublished,
          }
        : EMPTY,
    );
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: (input: TeamMemberCreateInput) =>
      editing
        ? api<{ member: TeamMember }>(`/admin/team/${editing.id}`, {
            method: 'PATCH',
            body: input,
          })
        : api<{ member: TeamMember }>('/admin/team', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'team'] });
      setOpen(false);
      toast({ title: editing ? 'Member updated' : 'Member added' });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'Save failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/admin/team/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'team'] }),
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Team</h1>
          <p className="text-sm text-muted-foreground">People shown on the team page.</p>
        </div>
        <Button onClick={() => onOpen(null)}>
          <Plus className="mr-2 h-4 w-4" /> New member
        </Button>
      </header>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Order</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (data?.members.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No members yet.
                </TableCell>
              </TableRow>
            )}
            {data?.members.map((m) => (
              <TableRow key={m.id}>
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{m.role}</TableCell>
                <TableCell>{m.order}</TableCell>
                <TableCell>
                  <Badge variant={m.isPublished ? 'default' : 'secondary'}>
                    {m.isPublished ? 'live' : 'hidden'}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => onOpen(m)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Remove ${m.name}?`)) remove.mutate(m.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit member' : 'New member'}</DialogTitle>
          </DialogHeader>
          <form
            id="team-form"
            onSubmit={form.handleSubmit((d) => save.mutate(d))}
            className="space-y-3"
          >
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input id="name" {...form.register('name')} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="role">Role</Label>
                <Input id="role" {...form.register('role')} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bio">Bio</Label>
              <Textarea id="bio" rows={4} {...form.register('bio')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="photoUrl">Photo URL</Label>
              <Input
                id="photoUrl"
                placeholder="https://… (optional)"
                {...form.register('photoUrl', {
                  setValueAs: (v: string) => (v === '' ? null : v),
                })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="order">Order</Label>
                <Input id="order" type="number" {...form.register('order', { valueAsNumber: true })} />
              </div>
              <div className="flex items-end gap-2">
                <Switch
                  id="isPublished"
                  checked={form.watch('isPublished')}
                  onCheckedChange={(v) => form.setValue('isPublished', v)}
                />
                <Label htmlFor="isPublished">Published</Label>
              </div>
            </div>
          </form>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="team-form" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
